import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Tab,
  Tabs,
  IconButton  
} from "@mui/material";
import { HighlightOff } from "@mui/icons-material";

import { CONTRACT_STATUS } from "../../constants";
import CircularProgress from "../generic/CircularProgress";
import { UploadFile } from "../../services/backend/UploadFileService";
import { getUploadDocumentText } from "../../services/backend/ContractExtractionService";
import { assertPdfNotEncrypted } from "../../services/pdf/validatePdf";

const ContractFormPanel = ({
  contractId,
  contractCode,
  designationUrl,
  fileUrl,
  handleGetDataStartup,
  liquidationUrl,
  loading,
  setDesignationUrl,
  setDocumentText,
  setFileUrl,
  setFileHashes,
  setLiquidationUrl,
  setLoading,
  setStartupUrl,
  startupUrl,
  status,
  setInfoSnack
}) => {
  const user = useSelector((state) => state.user.value);
  const [tabIndex, setTabIndex] = useState(0);

  // eslint-disable-next-line
  const version = useMemo(() => new Date().getTime(), [startupUrl, designationUrl, fileUrl]);
  const disabled = useMemo(() => {
    return [CONTRACT_STATUS.CLOSED].includes(status);
  }, [status]);

  const handleFileUpload = async (evt, type) => {
    if (evt.target.files.length === 0) return;

    const file = evt.target.files[0];
    const encryptedError = await assertPdfNotEncrypted(file);
    if (encryptedError) {
      evt.target.value = '';
      if (setInfoSnack) {
        setInfoSnack({ open: true, message: encryptedError, severity: 'error', duration: 5000 });
      }
      return;
    }

    setLoading(true);

    const response = await UploadFile(
      user,
      evt.target.files[0],
      process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS,
      contractId
        ? `${contractCode}/${contractCode}_${type}.pdf`
        : `temp/${Math.random().toString(36).slice(2)}.pdf`,
      type === "contract" || type === "startup"
    );

    if (response) {
      // Texto OCR de Document AI: dispara el autollenado con IA del formulario.
      // Leído con getUploadDocumentText porque la gfunction devuelve la key
      // `documentText` — este panel leía `response.documentData.text`, un alias
      // que la función dejó de devolver, y el autollenado quedó sin dispararse.
      const documentText = ['contract', 'startup'].includes(type)
        ? getUploadDocumentText(response)
        : null;

      if (['contract', 'startup'].includes(type) && !documentText && setInfoSnack) {
        setInfoSnack({
          open: true,
          message: 'No fue posible leer el texto del documento: diligencia los campos manualmente.',
          severity: 'info',
          duration: 6000,
        });
      }

      switch (type) {
        case 'contract':
          setFileUrl(response.downloadUrl);
          setDocumentText(documentText);
          setFileHashes((prev) => ({ ...prev, contract: response.fileHash }));
          break;

        case 'startup':
          setStartupUrl(response.downloadUrl);
          // Opcional: no todos los formularios que reutilizan el panel
          // autollenan desde el acta de inicio.
          if (documentText && handleGetDataStartup) handleGetDataStartup(documentText);
          setFileHashes((prev) => ({ ...prev, startup: response.fileHash }));
          break;

        case 'designation':
          setDesignationUrl(response.downloadUrl);
          setFileHashes((prev) => ({ ...prev, designation: response.fileHash }));
          break;

        case 'liquidation':
          setLiquidationUrl(response.downloadUrl);
          setFileHashes((prev) => ({ ...prev, liquidation: response.fileHash }));
          break;

        default:
          break;
      }
    }

    setLoading(false);
  };

  return (
    <>
      <Tabs value={tabIndex} onChange={(__, value) => setTabIndex(value)}>
        <Tab
          id="tab-contract"
          label={(
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Contrato
              {fileUrl && !disabled
                && <IconButton
                  sx={{ py: 0 }}
                  size="small"
                  onClick={() => setFileUrl('')}
                >
                  <HighlightOff />
                </IconButton>
              }
            </Box>
          )} />
        <Tab
          id="tab-startup"
          label={(
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Acta de inicio
              {startupUrl && !disabled
                && <IconButton
                  sx={{ py: 0 }}
                  size="small"
                  onClick={() => setStartupUrl('')}
                >
                  <HighlightOff />
                </IconButton>
              }
            </Box>
          )}
        />
        <Tab
          id="tab-designation"
          label={(
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Designación supervisor
              {designationUrl && !disabled
                && <IconButton
                  sx={{ py: 0 }}
                  size="small"
                  onClick={() => setDesignationUrl('')}
                >
                  <HighlightOff />
                </IconButton>
              }
            </Box>
          )}
        />
        <Tab
          id="tab-liquidation"
          label={(
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Acta de liquidación
              {liquidationUrl && status !== CONTRACT_STATUS.CLOSED
                && <IconButton
                  sx={{ py: 0 }}
                  size="small"
                  onClick={() => setLiquidationUrl('')}
                >
                  <HighlightOff />
                </IconButton>
              }
            </Box>
          )}
        />
      </Tabs>
      <div
        role="tabpanel"
        hidden={tabIndex !== 0}
        id="tabpanel-contract"
        value={tabIndex}
        index={0}
        style={{ height: 'calc(100% - 40px)', marginTop: '2px' }}
      >
        {fileUrl ? (
          <iframe
            title="File PDF"
            src={`${fileUrl}?v=${version}`}
            width="100%"
            height="98%"
            seamless="seamless"
            scrolling="no"
            frameBorder="1"
            allowFullScreen={true}
          />
        ) : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              backgroundColor: "lightgray",
              border: "2px dashed gray",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <label htmlFor="upload-contract">
              <Button
                variant="contained"
                component="span"
                disabled={loading || disabled}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Cargar Contrato"
                )}
              </Button>
              <input
                id="upload-contract"
                hidden
                accept="application/pdf"
                type="file"
                onChange={(evt) => handleFileUpload(evt, 'contract')}
              />
            </label>
          </Box>
        )}
      </div>
      <div
        role="tabpanel"
        hidden={tabIndex !== 1}
        id="tabpanel-startup"
        value={tabIndex}
        index={1}
        style={{ height: 'calc(100% - 40px)', marginTop: '2px' }}
      >
        {startupUrl ? (
          <>
            <iframe
              title="File PDF"
              src={`${startupUrl}?v=${version}`}
              width="100%"
              height="100%"
              seamless="seamless"
              scrolling="no"
              frameBorder="1"
              allowFullScreen={true}
            />
          </>
        ) : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              backgroundColor: "lightgray",
              border: "2px dashed gray",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <label htmlFor="upload-startup">
              <Button
                variant="contained"
                component="span"
                disabled={loading || disabled}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Cargar Acta de inicio"
                )}
              </Button>
              <input
                id="upload-startup"
                hidden
                accept="application/pdf"
                type="file"
                onChange={(evt) => handleFileUpload(evt, 'startup')}
              />
            </label>
          </Box>
        )}
      </div>
      <div
        role="tabpanel"
        hidden={tabIndex !== 2}
        id="tabpanel-designation"
        value={tabIndex}
        index={2}
        style={{ height: 'calc(100% - 40px)', marginTop: '2px' }}
      >
        {designationUrl ? (
          <>
            <iframe
              title="File PDF"
              src={`${designationUrl}?v=${version}`}
              width="100%"
              height="100%"
              seamless="seamless"
              scrolling="no"
              frameBorder="1"
              allowFullScreen={true}
            />
          </>
        ) : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              backgroundColor: "lightgray",
              border: "2px dashed gray",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <label htmlFor="upload-designation">
              <Button
                variant="contained"
                component="span"
                disabled={loading || disabled}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Cargar Designación Supervisor"
                )}
              </Button>
              <input
                id="upload-designation"
                hidden
                accept="application/pdf"
                type="file"
                onChange={(evt) => handleFileUpload(evt, 'designation')}
              />
            </label>
          </Box>
        )}
      </div>
      <div
        role="tabpanel"
        hidden={tabIndex !== 3}
        id="tabpanel-liquidation"
        value={tabIndex}
        index={3}
        style={{ height: 'calc(100% - 40px)', marginTop: '2px' }}
      >
        {liquidationUrl ? (
          <iframe
            title="File PDF"
            src={`${liquidationUrl}?v=${version}`}
            width="100%"
            height="98%"
            seamless="seamless"
            scrolling="no"
            frameBorder="1"
            allowFullScreen={true}
          />
        ) : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              backgroundColor: "lightgray",
              border: "2px dashed gray",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <label htmlFor="upload-liquidation">
              <Button
                variant="contained"
                component="span"
                disabled={loading || status === CONTRACT_STATUS.CLOSED}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Cargar Acta de liquidación"
                )}
              </Button>
              <input
                id="upload-liquidation"
                hidden
                accept="application/pdf"
                type="file"
                onChange={(evt) => handleFileUpload(evt, 'liquidation')}
              />
            </label>
          </Box>
        )}
      </div>
    </>
  );
};

export default ContractFormPanel;
